import React, { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useTour } from "../context/TourContext";
import { useUserRole } from "../context/UserRoleContext";
import { ColorblindImage } from "./ColorblindImage";
import {
  primaryIconNavItems,
  secondaryTextNavItems,
  accountNavItems,
} from "../constants/navigation";

interface MobileNavDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onStartTour: () => void;
}

const MobileNavDrawer: React.FC<MobileNavDrawerProps> = ({
  isOpen,
  onClose,
  onStartTour,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userRole } = useUserRole();
  const { isGuidedTourEnabled, setIsTourRunning } = useTour();
  const { logout, loginWithRedirect } = useAuth0();
  const isHomePage = location.pathname === "/";

  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  const handleNavigate = (path: string) => {
    navigate(path);
    onClose();
  };

  const handleLogin = () => {
    try {
      localStorage.setItem("preLoginPath", window.location.pathname);
    } catch (error) {
      console.error("Error accessing localStorage:", error);
    }
    loginWithRedirect({ appState: { returnTo: "/your-collections" } });
    onClose();
  };

  const handleLogout = () => {
    logout({ logoutParams: { returnTo: window.location.origin } });
    onClose();
  };

  const handleStartTour = () => {
    setIsTourRunning(true);
    if (location.pathname === "/name-generator") {
      window.setTimeout(() => onStartTour(), 350);
    } else {
      onStartTour();
    }
    onClose();
  };

  const linkClass =
    "flex min-h-[44px] w-full items-center rounded-lg px-3 py-2 text-left text-base font-semibold transition-colors hover:bg-gray-100 dark:hover:bg-gray-700";

  const isActivePath = (path: string) => location.pathname === path;

  return (
    <div className="fixed inset-0 z-[100] md:hidden" role="presentation">
      <button
        type="button"
        className="absolute inset-0 bg-black/50"
        aria-label="Close menu"
        onClick={onClose}
      />
      <nav
        className="absolute right-0 top-0 flex h-full w-[min(100vw-3rem,20rem)] flex-col bg-white shadow-xl dark:bg-gray-800"
        aria-label="Mobile navigation"
      >
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 dark:border-gray-700">
          <h2 className="text-lg font-bold text-black dark:text-white">Menu</h2>
          <button
            type="button"
            onClick={onClose}
            className="flex size-10 items-center justify-center rounded-lg text-2xl font-bold text-black hover:bg-gray-100 dark:text-white dark:hover:bg-gray-700"
            aria-label="Close menu"
          >
            ×
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-3 py-4">
          <p className="mb-2 px-3 text-xs font-bold uppercase tracking-wide text-gray-500">
            Main
          </p>
          <div className="mb-4 space-y-1">
            {primaryIconNavItems
              .filter((item) => item.roles.includes(userRole))
              .map((item) => (
                <button
                  key={item.path}
                  type="button"
                  onClick={() => handleNavigate(item.path)}
                  className={`${linkClass} gap-3 ${
                    isActivePath(item.path)
                      ? "bg-blue-50 text-blue-900 dark:bg-blue-900/30 dark:text-blue-100"
                      : "text-black dark:text-white"
                  }`}
                >
                  <ColorblindImage
                    src={item.iconSrc}
                    alt=""
                    className="size-8 shrink-0"
                  />
                  {item.label}
                </button>
              ))}
          </div>

          <p className="mb-2 px-3 text-xs font-bold uppercase tracking-wide text-gray-500">
            Explore
          </p>
          <div className="mb-4 space-y-1">
            {secondaryTextNavItems
              .filter(
                (item) =>
                  item.roles.includes(userRole) &&
                  item.path !== "/your-collections",
              )
              .map((item) => (
                <button
                  key={item.path}
                  type="button"
                  onClick={() => handleNavigate(item.path)}
                  className={`${linkClass} ${
                    isActivePath(item.path)
                      ? "bg-blue-50 text-blue-900 dark:bg-blue-900/30 dark:text-blue-100"
                      : "text-black dark:text-white"
                  }`}
                >
                  {item.label}
                </button>
              ))}
          </div>

          <p className="mb-2 px-3 text-xs font-bold uppercase tracking-wide text-gray-500">
            Account
          </p>
          <div className="mb-4 space-y-1">
            {accountNavItems
              .filter((item) => item.roles.includes(userRole))
              .map((item) => (
                <button
                  key={item.path}
                  type="button"
                  onClick={() => handleNavigate(item.path)}
                  className={`${linkClass} ${
                    isActivePath(item.path)
                      ? "bg-blue-50 text-blue-900 dark:bg-blue-900/30 dark:text-blue-100"
                      : "text-black dark:text-white"
                  }`}
                >
                  {item.label}
                </button>
              ))}
          </div>
        </div>

        <div className="space-y-2 border-t border-gray-200 px-3 py-4 dark:border-gray-700">
          {isGuidedTourEnabled && (
            <button
              type="button"
              onClick={handleStartTour}
              className="start-tour-button w-full rounded-lg border border-black bg-blue-500 px-4 py-3 text-base font-bold text-white transition-colors hover:bg-blue-600"
            >
              Start Tour
            </button>
          )}
          <button
            type="button"
            onClick={isHomePage ? handleLogin : handleLogout}
            className="w-full rounded-lg border border-black bg-indigo-500 px-4 py-3 text-base font-bold text-white transition-colors hover:bg-indigo-600"
          >
            {isHomePage ? "Login" : "Logout"}
          </button>
        </div>
      </nav>
    </div>
  );
};

export default MobileNavDrawer;
